/**
 * Lifts data hooks.
 *
 * Provides:
 * - `useLifts()` — site-scoped TanStack Query for lift live state
 *
 * The REST seed reads the real v1 `lifts` category snapshot
 * (`fetchLifts` → `v1-zenoh-client.ts`, via `liftsQueryOptions` so
 * the query key is the same site-/category-/epoch-aware `v1Key()`
 * the live overlay in `useLiveData` writes into — plan §6.2).
 *
 * The `lifts_registry` read alongside it is what decides which lifts
 * EXIST, exactly as `useDoors` does for doors — the two device lists
 * follow one rule so that "nothing is publishing state for this car"
 * never again renders as "there is no such car". Geometry (ref
 * position, width, depth, level list) already reached LIVE rows
 * through `fetchLifts`, which reads the same registry for that
 * purpose; what is new here is the rows that live state does not
 * mention at all. See `registry-merge.ts`.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import {
  liftsQueryOptions,
  liftsRegistryQueryOptions,
  liftWriteGateQueryOptions,
} from '../query-options';
import { mergeLiftsWithRegistry } from '../registry-merge';
import {
  acquireLiftSession,
  armLiftWrites,
  liftAbort,
  liftCommand,
  renewLiftSession,
  liftAdvance,
  liftRecover,
  liftEndSession,
  liftCall,
  liftClose,
} from '../v1-client';
import type {
  AcquireLiftSessionResponse,
  LiftRegistry,
  LiftArmRequest,
  LiftArmResponse,
  LiftAbortRequest,
  LiftAbortResponse,
  LiftCommandRequest,
  LiftCommandResponse,
  LiftWriteGate,
  SiteId,
  LiftAdvanceRequest,
  LiftRecoverRequest,
  LiftEndSessionRequest,
  LiftManualCallRequest,
  LiftManualCloseRequest,
  LiftVerbResponse,
} from '../v1';
import type { LiftViewModel } from '../adapters';

export function useLifts() {
  const siteId = useSafeSiteId();
  const site = siteId ? (siteId as SiteId) : null;
  const live = useQuery<LiftViewModel[]>(liftsQueryOptions(site));
  // Its own query and its own key, for the reason spelled out in
  // `useDoors`: the lifts key is written by `useLiveData`'s SSE flush
  // too, so a registry merged in underneath it would not survive the
  // next live event.
  const registry = useQuery<LiftRegistry[]>(liftsRegistryQueryOptions(site));
  const data = useMemo(
    () => mergeLiftsWithRegistry(live.data, registry.data),
    [live.data, registry.data],
  );
  return { ...live, data };
}

// ----------------------------------------------------------------------------
// Server-owned lift session + command (plan §Command lifecycle: "Disallow
// arbitrary client session IDs and disable controls when ownership cannot
// be reconstructed.")
// ----------------------------------------------------------------------------

export function useAcquireLiftSession() {
  const siteId = useSafeSiteId();
  return useMutation<AcquireLiftSessionResponse, Error, string>({
    mutationFn: (liftName: string) =>
      acquireLiftSession(siteId as SiteId, {
        lift: { site: siteId as SiteId, lift: liftName },
        command_id: crypto.randomUUID(),
      }),
  });
}

export function useRenewLiftSession() {
  const siteId = useSafeSiteId();
  return useMutation<AcquireLiftSessionResponse, Error, string>({
    mutationFn: (sessionId: string) => renewLiftSession(siteId as SiteId, sessionId),
  });
}

// ----------------------------------------------------------------------------
// The five OPERATOR verbs. Each resolves to a `LiftVerbResponse` whose
// `request_id` the drawer's `useOperatorRequest` then waits on. NONE of
// them invalidates the lifts query: the projection is the truth and it
// arrives over SSE within a second of the adapter's ack -- a refetch
// here would only race it with a snapshot that may predate the verdict.
// ----------------------------------------------------------------------------

/**
 * "Release lift" — advance a stalled session by one wait.
 *
 * Separate from `useLiftCommand` for the same reason the abort is: it
 * takes no lift session, because the ride it unsticks belongs to
 * somebody else.
 */
export function useLiftAdvance() {
  const siteId = useSafeSiteId();
  return useMutation<LiftVerbResponse, Error, { lift: string; body: LiftAdvanceRequest }>({
    mutationFn: (vars) => liftAdvance(siteId as SiteId, vars.lift, vars.body),
  });
}

/** "Recover stuck robot" — session ended, car released, task cancelled. */
export function useLiftRecover() {
  const siteId = useSafeSiteId();
  return useMutation<LiftVerbResponse, Error, { lift: string; body: LiftRecoverRequest }>({
    mutationFn: (vars) => liftRecover(siteId as SiteId, vars.lift, vars.body),
  });
}

/** "End RMF session" — the teardown, which does not move a robot first. */
export function useLiftEndSession() {
  const siteId = useSafeSiteId();
  return useMutation<LiftVerbResponse, Error, { lift: string; body: LiftEndSessionRequest }>({
    mutationFn: (vars) => liftEndSession(siteId as SiteId, vars.lift, vars.body),
  });
}

/**
 * "Request Floor" — the operator's own call of the car, as an
 * operator-owned one-shot session at the adapter. Replaces
 * `useLiftCommand({action: 'request_destination'})`, which is retired
 * (see `liftCall` for the loop it caused).
 */
export function useLiftCall() {
  const siteId = useSafeSiteId();
  return useMutation<LiftVerbResponse, Error, { lift: string; body: LiftManualCallRequest }>({
    mutationFn: (vars) => liftCall(siteId as SiteId, vars.lift, vars.body),
  });
}

/** "Cancel call" — end the operator-owned session. */
export function useLiftClose() {
  const siteId = useSafeSiteId();
  return useMutation<LiftVerbResponse, Error, { lift: string; body: LiftManualCloseRequest }>({
    mutationFn: (vars) => liftClose(siteId as SiteId, vars.lift, vars.body),
  });
}

/**
 * The RECOVERY verb. Separate hook because it is a separate route with a
 * separate capability — see `LiftAbortRequest` for why it is not another
 * `action` on `useLiftCommand`.
 */
export function useLiftAbort() {
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  return useMutation<LiftAbortResponse, Error, { lift: string; body: LiftAbortRequest }>({
    mutationFn: (vars) => liftAbort(siteId as SiteId, vars.lift, vars.body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: liftsQueryOptions(siteId ? (siteId as SiteId) : null).queryKey }),
  });
}

export function useLiftCommand() {
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  return useMutation<LiftCommandResponse, Error, { lift: string; body: LiftCommandRequest }>({
    mutationFn: (vars) => liftCommand(siteId as SiteId, vars.lift, vars.body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: liftsQueryOptions(siteId ? (siteId as SiteId) : null).queryKey }),
  });
}

// ----------------------------------------------------------------------------
// Write gate (tier 1 validation + tier 2 arming)
// ----------------------------------------------------------------------------

/**
 * The site's lift write gate. ONE query for the whole site, and every
 * lift surface reads this same one -- the table's per-lift column, the
 * panel above it, and the detail drawer. Reading arm state twice is
 * the exact shape of the bug this change exists to fix; two hooks
 * disagreeing about it in one screen would be that bug again, in a
 * cheaper place.
 */
export function useLiftWriteGate() {
  const siteId = useSafeSiteId();
  // `LiftWriteGate | null`, and callers must keep the two apart:
  // `null` is a settled 404 -- this api-server publishes no write gate
  // -- while `undefined` is the query still in flight or failed. Only
  // one of the three is a gate reading, and the verdict functions in
  // `lift-dispatch-status.ts` are where that distinction is spent.
  return useQuery<LiftWriteGate | null>(
    liftWriteGateQueryOptions(siteId ? (siteId as SiteId) : null),
  );
}

/**
 * Arm or disarm the site's lift bridge.
 *
 * Invalidates the lifts query alongside the gate: a disarm makes the
 * cloud adapter publish MODE_OFFLINE for every lift on the site, so
 * leaving the lift rows on their pre-arm snapshot would show an
 * "Armed" gate over six lifts still reporting offline, or the reverse.
 */
export function useArmLiftWrites() {
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  return useMutation<LiftArmResponse, Error, LiftArmRequest>({
    mutationFn: (body) => armLiftWrites(siteId as SiteId, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: liftWriteGateQueryOptions(siteId ? (siteId as SiteId) : null).queryKey,
      });
      void qc.invalidateQueries({
        queryKey: liftsQueryOptions(siteId ? (siteId as SiteId) : null).queryKey,
      });
    },
  });
}

/* REMOVED 2026-08-29: `LiftRequest`, `liftDoorModeToInt`, `requestLift`
 * and `useRequestLift`.
 *
 * They POSTed straight to the UPSTREAM rmf-api-server gateway
 * (`rmfEndpoints.liftRequest`), bypassing the entire v1 command stack:
 * no lift session, no capability check, no idempotency key, no
 * `CommandIntent` audit row, no worker-epoch fence. Grep found zero call
 * sites -- it was a leftover from the upstream port, one import away
 * from being used by someone who assumed it was the supported path.
 *
 * The supported path is `useLiftCommand()` above
 * (`POST /api/v1/sites/{site}/lifts/{lift}/command`).
 */
